import { useEffect, useState } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Leaf, ShieldCheck, TrendingUp } from "lucide-react";
import { LeanWordmark } from "../components/Logo";
import { api } from "../lib/api";
import { GOOGLE_CLIENT_ID } from "../lib/config";

export default function Login() {
  const { user, loading, setUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [error] = useState(location.state?.error || null);

  useEffect(() => {
    if (!loading && user) navigate("/dashboard");
  }, [user, loading, navigate]);

  const [busy, setBusy] = useState(false);
  const [loginError, setLoginError] = useState(null);

  const handleGoogleLogin = () => {
    setLoginError(null);
    const gis = window.google?.accounts?.oauth2;
    if (!gis) { setLoginError("Google sign-in is still loading. Please try again in a moment."); return; }
    const client = gis.initTokenClient({
      client_id: GOOGLE_CLIENT_ID,
      scope: "openid email profile",
      callback: async (resp) => {
        if (!resp || resp.error || !resp.access_token) { setBusy(false); return; }
        try {
          const res = await api.post("/auth/google", { access_token: resp.access_token });
          setUser(res.data);
          navigate("/dashboard");
        } catch (e) {
          setLoginError(e?.response?.data?.detail || "Login failed");
          setBusy(false);
        }
      },
      error_callback: () => setBusy(false),
    });
    setBusy(true);
    client.requestAccessToken({ prompt: "select_account" });
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      {/* Left: form */}
      <div className="flex items-center justify-center p-6 sm:p-10">
        <div className="w-full max-w-md animate-fade-up">
          <Link to="/" className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-500 hover:text-slate-900 mb-8" data-testid="back-to-site-link">
            ← Back to site
          </Link>
          <div className="mb-10" data-testid="app-logo">
            <LeanWordmark className="h-14 w-auto" />
            <p className="text-xs text-muted-foreground mt-2 ml-1">Employee Login</p>
          </div>

          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900 font-head">Welcome back.</h1>
          <p className="mt-3 text-muted-foreground text-sm leading-relaxed">
            Sign in with your authorised Google account to access the internal staff workspace.
          </p>

          {(loginError || error) && (
            <div className="mt-6 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700" data-testid="login-error">
              {loginError || error}
            </div>
          )}

          <button
            onClick={handleGoogleLogin}
            disabled={busy}
            data-testid="google-login-button"
            className="mt-8 w-full flex items-center justify-center gap-3 rounded-xl border border-slate-200 bg-white px-5 py-3.5 font-semibold text-slate-700 shadow-sm hover:bg-slate-50 hover:border-slate-300 transition-colors"
          >
            <svg className="h-5 w-5" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
            Continue with Google
          </button>

          <div className="mt-6 flex items-center gap-2 text-xs text-muted-foreground">
            <ShieldCheck className="h-4 w-4 text-emerald-600" />
            Private &amp; secure. Only authorized accounts can access this portal.
          </div>
        </div>
      </div>

      {/* Right: brand panel */}
      <div className="relative hidden lg:block overflow-hidden bg-slate-900">
        <img
          src="https://images.unsplash.com/photo-1675270882554-ab6817fb44f3?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
          alt="Wellness"
          className="absolute inset-0 h-full w-full object-cover opacity-30"
        />
        <div className="relative h-full flex flex-col justify-end p-12 text-white">
          <TrendingUp className="h-10 w-10 text-indigo-300 mb-6" />
          <h2 className="text-3xl font-extrabold font-head leading-tight max-w-md">
            Your whole business, in one clean ledger.
          </h2>
          <p className="mt-4 text-slate-300 text-sm max-w-md leading-relaxed">
            Income, expenses, client dues, brand collabs, invoices and a live Profit &amp; Loss — with one-tap WhatsApp summaries and Excel backup.
          </p>
        </div>
      </div>
    </div>
  );
}
